import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import {
  BarChart3,
  Filter,
  Download,
  FileSpreadsheet,
  FileText,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Loader2,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Activity,
  Bell,
  Mail,
  AlertCircle
} from 'lucide-react';
import { format, parseISO, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TourAnalysis = () => {
  const { i18n } = useTranslation();
  const { isDemoReadonly } = useAuth();
  // Filter states - default to 2025 for demo users
  const [startDate, setStartDate] = useState(isDemoReadonly() ? '2025-01-01' : '');
  const [endDate, setEndDate] = useState(isDemoReadonly() ? '2025-12-31' : '');
  const [supervisorId, setSupervisorId] = useState('');
  const [shift, setShift] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [status, setStatus] = useState('');
  
  // Data states
  const [tours, setTours] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Alert states
  const [alertStatus, setAlertStatus] = useState(null);
  const [checkingAlerts, setCheckingAlerts] = useState(false);
  
  // Dropdown options
  const [supervisors, setSupervisors] = useState([]);
  const [workShifts, setWorkShifts] = useState(['Jour', 'Soir', 'Nuit']);
  const [zones, setZones] = useState([]);
  const [sites, setSites] = useState([]);
  
  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Load initial data
  useEffect(() => {
    loadDropdownOptions();
    // Set default dates - for demo, use 2025; otherwise last 30 days
    if (!isDemoReadonly()) {
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      setEndDate(format(today, 'yyyy-MM-dd'));
      setStartDate(format(thirtyDaysAgo, 'yyyy-MM-dd'));
    }
  }, []);
  
  // Load analysis when filters change
  useEffect(() => {
    if (startDate && endDate) {
      loadAnalysis();
    }
  }, [startDate, endDate]);
  
  const loadDropdownOptions = async () => {
    try {
      const [supervisorsRes, shiftsRes, zonesRes, sitesRes] = await Promise.all([
        axios.get(`${API}/analysis/supervisors`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/work-shifts`, { headers: getAuthHeaders() }).catch(() => ({ data: { shifts: ['Jour', 'Soir', 'Nuit'] } })),
        axios.get(`${API}/zones`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/sites`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      
      setSupervisors(supervisorsRes.data || []);
      setWorkShifts(shiftsRes.data?.shifts || shiftsRes.data?.work_shifts || ['Jour', 'Soir', 'Nuit']);
      setZones(zonesRes.data || []);
      setSites(sitesRes.data || []);
    } catch (error) {
      console.error('Error loading dropdown options:', error);
    }
  };
  
  const loadAnalysis = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (supervisorId) params.append('supervisor_id', supervisorId);
      if (shift) params.append('shift', shift);
      if (zoneId) params.append('zone_id', zoneId);
      if (siteId) params.append('site_id', siteId);
      if (status) params.append('status', status);
      
      const response = await axios.get(`${API}/analysis/tours?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      
      setTours(response.data.tours || []);
      setKpis(response.data.kpis || null);
      setCurrentPage(1); // Reset pagination when data changes
    } catch (error) {
      console.error('Error loading analysis:', error);
      toast.error('Erreur lors du chargement de l\'analyse');
    } finally {
      setLoading(false);
    }
  };
  
  const checkAlerts = async () => {
    setCheckingAlerts(true);
    try {
      const response = await axios.post(`${API}/alerts/check-thresholds`, {}, {
        headers: getAuthHeaders()
      });
      setAlertStatus(response.data);
      
      if (response.data.emails_sent?.length > 0) {
        toast.success(`${response.data.emails_sent.length} alerte(s) envoyée(s) par email`);
      } else if (response.data.trs?.alert_triggered || response.data.lean?.alert_triggered) {
        if (response.data.trs?.already_notified || response.data.lean?.already_notified) {
          toast.info('Alertes déjà envoyées aujourd\'hui');
        }
      } else {
        toast.success('Aucune alerte nécessaire - indicateurs conformes');
      }
    } catch (error) {
      console.error('Error checking alerts:', error);
      if (error.response?.status === 403) {
        toast.error('Accès réservé aux administrateurs');
      } else {
        toast.error('Erreur lors de la vérification des alertes');
      }
    } finally {
      setCheckingAlerts(false);
    }
  };
  
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('language', i18n.language || 'fr');
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (supervisorId) params.append('supervisor_id', supervisorId);
      if (shift) params.append('shift', shift);
      if (zoneId) params.append('zone_id', zoneId);
      if (siteId) params.append('site_id', siteId);
      if (status) params.append('status', status);
      
      const response = await axios.get(`${API}/analysis/export/${format}?${params.toString()}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analyse_tournees_${startDate}_${endDate}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Erreur lors de l'export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  
  const sortedTours = [...tours].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (sortField === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
  
  // Pagination
  const totalPages = Math.ceil(sortedTours.length / pageSize);
  const paginatedTours = sortedTours.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  
  const resetFilters = () => {
    setSupervisorId('');
    setShift('');
    setZoneId('');
    setSiteId('');
    setStatus('');
    setCurrentPage(1); // Reset pagination too
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setEndDate(format(today, 'yyyy-MM-dd'));
    setStartDate(format(thirtyDaysAgo, 'yyyy-MM-dd'));
  };
  
  // Calculate trend data for the chart
  const trendData = useMemo(() => {
    if (!tours.length || !startDate || !endDate) return [];
    
    try {
      // Group tours by date
      const dailyData = {};
      
      tours.forEach(tour => {
        const date = tour.created_at?.split('T')[0];
        if (!date) return;
        
        if (!dailyData[date]) {
          dailyData[date] = {
            trsValues: [],
            leanValues: [],
            count: 0
          };
        }
        
        if (tour.trs_score > 0) {
          dailyData[date].trsValues.push(tour.trs_score);
        }
        dailyData[date].leanValues.push(tour.lean_score || 0);
        dailyData[date].count++;
      });
      
      // Convert to array and calculate averages
      const result = Object.entries(dailyData)
        .map(([date, data]) => ({
          date,
          dateFormatted: format(parseISO(date), 'dd/MM', { locale: fr }),
          trs: data.trsValues.length > 0 
            ? Math.round(data.trsValues.reduce((a, b) => a + b, 0) / data.trsValues.length * 10) / 10
            : null,
          lean: data.leanValues.length > 0
            ? Math.round(data.leanValues.reduce((a, b) => a + b, 0) / data.leanValues.length * 10) / 10
            : 0,
          tours: data.count
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
      
      return result;
    } catch (e) {
      console.error('Error calculating trend data:', e);
      return [];
    }
  }, [tours, startDate, endDate]);
  
  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold text-slate-800">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value !== null ? `${entry.value}%` : 'N/A'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  const getStatusBadge = (tour) => {
    if (tour.status === 'verrouillee') {
      return <Badge className="bg-green-100 text-green-800">Validée</Badge>;
    } else if (tour.status === 'a_analyser') {
      return <Badge className="bg-amber-100 text-amber-800">À analyser</Badge>;
    } else if (tour.needs_analysis) {
      return <Badge className="bg-red-100 text-red-800">Escalade</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600">{tour.status}</Badge>;
  };
  
  const getTrsColor = (trs, threshold) => {
    if (trs === 0) return 'text-slate-400';
    if (trs < threshold) return 'text-red-600 font-semibold';
    return 'text-green-600';
  };
  
  const getLeanColor = (lean, threshold) => {
    if (lean < threshold) return 'text-red-600 font-semibold';
    return 'text-green-600';
  };
  
  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? 
      <ChevronUp className="w-4 h-4 inline ml-1" /> : 
      <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  return (
    <div className="p-6 space-y-6" data-testid="tour-analysis-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="page-title">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Analyse des Tournées
          </h1>
          <p className="text-slate-600 mt-1">
            Analysez les performances des tournées avec des filtres avancés
          </p>
        </div>
        
        {/* Export Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport('excel')}
            disabled={exporting || tours.length === 0}
            data-testid="export-excel-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4 mr-2" />
            )}
            Export Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={exporting || tours.length === 0}
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
          <Button
            variant="default"
            onClick={checkAlerts}
            disabled={checkingAlerts}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="check-alerts-btn"
          >
            {checkingAlerts ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Vérifier Alertes
          </Button>
        </div>
      </div>
      
      {/* Alert Status Card */}
      {alertStatus && (
        <Card className="border-amber-200 bg-amber-50" data-testid="alert-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              Statut des Alertes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* TRS Alert Status */}
              <div className={`p-4 rounded-lg border ${alertStatus.trs?.alert_triggered ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {alertStatus.trs?.alert_triggered ? (
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                  <span className="font-semibold">TRS</span>
                </div>
                <p className="text-sm text-slate-600">
                  {alertStatus.trs?.below_days > 0 
                    ? `${alertStatus.trs.below_days} jour(s) sous le seuil de ${alertStatus.trs.threshold}%`
                    : `Conforme (seuil: ${alertStatus.trs?.threshold}%)`
                  }
                </p>
                {alertStatus.trs?.already_notified && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Alerte déjà envoyée aujourd'hui
                  </p>
                )}
              </div>
              
              {/* Lean Alert Status */}
              <div className={`p-4 rounded-lg border ${alertStatus.lean?.alert_triggered ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {alertStatus.lean?.alert_triggered ? (
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                  <span className="font-semibold">Score Lean</span>
                </div>
                <p className="text-sm text-slate-600">
                  {alertStatus.lean?.below_days > 0 
                    ? `${alertStatus.lean.below_days} jour(s) sous le seuil de ${alertStatus.lean.threshold}%`
                    : `Conforme (seuil: ${alertStatus.lean?.threshold}%)`
                  }
                </p>
                {alertStatus.lean?.already_notified && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Alerte déjà envoyée aujourd'hui
                  </p>
                )}
              </div>
            </div>
            
            {/* Emails sent */}
            {alertStatus.emails_sent?.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {alertStatus.emails_sent.length} email(s) d'alerte envoyé(s) aux managers
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div className="space-y-2">
              <Label>Date début</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Date fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            
            {/* Supervisor */}
            <div className="space-y-2">
              <Label>Superviseur</Label>
              <Select value={supervisorId || "all"} onValueChange={(v) => setSupervisorId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-supervisor">
                  <SelectValue placeholder="Tous les superviseurs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les superviseurs</SelectItem>
                  {supervisors.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Shift */}
            <div className="space-y-2">
              <Label>Quart</Label>
              <Select value={shift || "all"} onValueChange={(v) => setShift(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-shift">
                  <SelectValue placeholder="Tous les quarts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les quarts</SelectItem>
                  {workShifts.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Site */}
            <div className="space-y-2">
              <Label>Site</Label>
              <Select value={siteId || "all"} onValueChange={(v) => { setSiteId(v === "all" ? "" : v); setZoneId(''); }}>
                <SelectTrigger data-testid="filter-site">
                  <SelectValue placeholder="Tous les sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les sites</SelectItem>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Zone */}
            <div className="space-y-2">
              <Label>Machine / Zone</Label>
              <Select value={zoneId || "all"} onValueChange={(v) => setZoneId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-zone">
                  <SelectValue placeholder="Toutes les zones" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les zones</SelectItem>
                  {zones
                    .filter(z => !siteId || z.site_id === siteId)
                    .map((z) => (
                      <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status */}
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="verrouillee">Validée</SelectItem>
                  <SelectItem value="a_analyser">À analyser</SelectItem>
                  <SelectItem value="escalade">Escalade</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-end gap-2">
              <Button 
                onClick={loadAnalysis} 
                disabled={loading} 
                data-testid="apply-filters-btn"
                className="bg-[#0E346E] hover:bg-[#0a2854] text-white"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Valider les filtres
              </Button>
              <Button variant="outline" onClick={resetFilters} data-testid="reset-filters-btn">
                Réinitialiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* KPIs Cards */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="kpis-section">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-800">{kpis.total_tours}</p>
                <p className="text-sm text-blue-600">Tournées analysées</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${kpis.moyenne_trs >= kpis.trs_threshold ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  {kpis.moyenne_trs >= kpis.trs_threshold ? (
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  )}
                  <p className={`text-3xl font-bold ${kpis.moyenne_trs >= kpis.trs_threshold ? 'text-green-800' : 'text-red-800'}`}>
                    {kpis.moyenne_trs}%
                  </p>
                </div>
                <p className={`text-sm ${kpis.moyenne_trs >= kpis.trs_threshold ? 'text-green-600' : 'text-red-600'}`}>
                  Moyenne TRS (seuil: {kpis.trs_threshold}%)
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${kpis.moyenne_lean >= kpis.lean_threshold ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1">
                  {kpis.moyenne_lean >= kpis.lean_threshold ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  )}
                  <p className={`text-3xl font-bold ${kpis.moyenne_lean >= kpis.lean_threshold ? 'text-green-800' : 'text-red-800'}`}>
                    {kpis.moyenne_lean}%
                  </p>
                </div>
                <p className={`text-sm ${kpis.moyenne_lean >= kpis.lean_threshold ? 'text-green-600' : 'text-red-600'}`}>
                  Moyenne Lean (seuil: {kpis.lean_threshold}%)
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-800">{kpis.total_ecarts}</p>
                <p className="text-sm text-amber-600">Total écarts</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${kpis.pct_below_threshold > 20 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className={`text-3xl font-bold ${kpis.pct_below_threshold > 20 ? 'text-red-800' : 'text-slate-800'}`}>
                  {kpis.pct_below_threshold}%
                </p>
                <p className={`text-sm ${kpis.pct_below_threshold > 20 ? 'text-red-600' : 'text-slate-600'}`}>
                  Sous seuil
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Trend Chart */}
      {kpis && trendData.length >= 1 && (
        <Card data-testid="trend-chart-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Évolution des indicateurs
            </CardTitle>
            <CardDescription>
              Tendance du TRS et du Score Lean sur la période sélectionnée
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="dateFormatted" 
                    tick={{ fontSize: 12 }}
                    stroke="#64748b"
                  />
                  <YAxis 
                    domain={[0, 100]}
                    tick={{ fontSize: 12 }}
                    stroke="#64748b"
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  {/* Reference lines for thresholds */}
                  <ReferenceLine 
                    y={kpis.trs_threshold} 
                    stroke="#3b82f6" 
                    strokeDasharray="5 5" 
                    label={{ value: `Seuil TRS (${kpis.trs_threshold}%)`, fill: '#3b82f6', fontSize: 11 }}
                  />
                  <ReferenceLine 
                    y={kpis.lean_threshold} 
                    stroke="#10b981" 
                    strokeDasharray="5 5"
                    label={{ value: `Seuil Lean (${kpis.lean_threshold}%)`, fill: '#10b981', fontSize: 11 }}
                  />
                  
                  <Line 
                    type="monotone" 
                    dataKey="trs" 
                    name="TRS" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />
                  <Line 
                    type="monotone" 
                    dataKey="lean" 
                    name="Score Lean" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={{ fill: '#10b981', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Détail des tournées
            {tours.length > 0 && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({tours.length} résultat{tours.length > 1 ? 's' : ''})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : tours.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune tournée trouvée pour les filtres sélectionnés</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="tours-table">
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('created_at')}
                    >
                      Date <SortIcon field="created_at" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('zone_name')}
                    >
                      Machine/Zone <SortIcon field="zone_name" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('supervisor_name')}
                    >
                      Superviseur <SortIcon field="supervisor_name" />
                    </TableHead>
                    <TableHead>Quart</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 text-center"
                      onClick={() => handleSort('trs_score')}
                    >
                      TRS (%) <SortIcon field="trs_score" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 text-center"
                      onClick={() => handleSort('lean_score')}
                    >
                      Score Lean (%) <SortIcon field="lean_score" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 text-center"
                      onClick={() => handleSort('nok_count')}
                    >
                      Écarts <SortIcon field="nok_count" />
                    </TableHead>
                    <TableHead className="text-center">Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTours.map((tour) => (
                    <TableRow 
                      key={tour.id}
                      className={tour.is_below_trs_threshold || tour.is_below_lean_threshold ? 'bg-red-50' : ''}
                    >
                      <TableCell className="font-medium">
                        {tour.created_at ? format(new Date(tour.created_at), 'dd/MM/yyyy', { locale: fr }) : '-'}
                      </TableCell>
                      <TableCell>{tour.zone_name || '-'}</TableCell>
                      <TableCell>{tour.supervisor_name || '-'}</TableCell>
                      <TableCell>
                        {tour.shift ? (
                          <Badge variant="outline">{tour.shift}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell className={`text-center ${getTrsColor(tour.trs_score, kpis?.trs_threshold || 85)}`}>
                        {tour.trs_score > 0 ? `${tour.trs_score}%` : '-'}
                      </TableCell>
                      <TableCell className={`text-center ${getLeanColor(tour.lean_score, kpis?.lean_threshold || 80)}`}>
                        {tour.lean_score > 0 ? `${tour.lean_score}%` : '0%'}
                      </TableCell>
                      <TableCell className="text-center">
                        {tour.nok_count > 0 ? (
                          <span className="text-red-600 font-semibold">{tour.nok_count}</span>
                        ) : (
                          <span className="text-green-600">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStatusBadge(tour)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t pt-4 mt-4">
                  <div className="text-sm text-slate-500">
                    Affichage {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, sortedTours.length)} sur {sortedTours.length} tournées
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setCurrentPage(1); }}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 / page</SelectItem>
                        <SelectItem value="20">20 / page</SelectItem>
                        <SelectItem value="50">50 / page</SelectItem>
                        <SelectItem value="100">100 / page</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Précédent
                    </Button>
                    <span className="text-sm px-2">
                      Page {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TourAnalysis;
